import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SmartImageProps {
  /** URL de l'image. null/undefined/'' → affiche le fallback. */
  src: string | null | undefined;
  alt: string;
  /**
   * Classe de ratio/taille du conteneur, typiquement une valeur d'IMAGE_SLOT
   * (ex. IMAGE_SLOT.publicCard = 'aspect-[4/3]', IMAGE_SLOT.listThumb = 'size-12').
   */
  ratio: string;
  /** 'cover' (photos, remplit + rogne) | 'contain' (produits/logos, entier). Défaut 'cover'. */
  fit?: 'cover' | 'contain';
  /** Padding autour de l'image en mode contain. Défaut 'p-4' (mettre 'p-0.5' pour une vignette). */
  containPadding?: string;
  /** CSS object-position (ex. FOCAL.people). Ignoré quand fit='contain'. Défaut 'center'. */
  position?: string;
  /** Classes du conteneur extérieur (rounded, border, bg override, etc.). */
  className?: string;
  /** Classes additionnelles sur l'<img> (rare ; ex. p-1 pour un petit logo). */
  imgClassName?: string;
  /** Élément affiché quand il n'y a pas de src (icône neutre par défaut). */
  fallback?: ReactNode;
  /** Zoom au survol (nécessite un parent `group`). Défaut false. */
  hoverZoom?: boolean;
  /** Attribut loading de l'<img>. Défaut 'lazy'. */
  loading?: 'eager' | 'lazy';
  /** Overlays rendus DANS le même conteneur relatif (gradients, badges, ring…). */
  children?: ReactNode;
}

/**
 * Image standardisée : conteneur à ratio fixe + image cadrée de façon cohérente.
 *
 * - Responsive par construction : le ratio déduit la hauteur de la largeur
 *   fluide → fonctionne identiquement sur cellulaire et ordinateur, sans media
 *   query ni hauteur fixe en pixels.
 * - `fit='cover'` (défaut) remplit et rogne en respectant `position` (focal point).
 * - `fit='contain'` montre l'image entière (bouteilles, logos) avec un léger padding.
 * - Affiche un `fallback` centré quand `src` est vide.
 */
export default function SmartImage({
  src,
  alt,
  ratio,
  fit = 'cover',
  containPadding = 'p-4',
  position = 'center',
  className,
  imgClassName,
  fallback,
  hoverZoom = false,
  loading = 'lazy',
  children,
}: SmartImageProps) {
  const isContain = fit === 'contain';

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-[#0A0A0A]',
        ratio,
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading={loading}
          style={isContain ? undefined : { objectPosition: position }}
          className={cn(
            'absolute inset-0 h-full w-full',
            isContain ? cn('object-contain', containPadding) : 'object-cover',
            hoverZoom &&
              'transition-transform duration-500 ease-out group-hover:scale-105',
            imgClassName,
          )}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-[#3A3A3A]">
          {fallback ?? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-1/3 w-1/3 max-h-12 max-w-12"
              aria-hidden="true"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            </svg>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
